import {
  getPaginationNextPageContext,
  getPaginationNextPageParameters,
  type PaginationPageParameters,
} from "@src/appV2/api/pagination";
import {
  useInfiniteGetQuery,
  type UseInfiniteGetQueryOptions,
} from "@src/appV2/api/useInfiniteGetQuery";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type AxiosError } from "axios";
import { z } from "zod";

import {
  newsFeedItemIncludedSchema,
  newsFeedItemPostEventSchema,
  newsFeedItemRecommendationEventSchema,
  newsFeedItemSchema,
  newsFeedItemTypeSchema,
} from "../types";
import { NEWS_FEED_URL } from "./constants";
import { useIsNewsFeedEnabled } from "./useIsNewsFeedEnabled";

export const newsFeedItemsParamsSchema = z.object({
  filter: z
    .object({
      "metadata.event": z
        .object({
          eq: z.union([newsFeedItemPostEventSchema, newsFeedItemRecommendationEventSchema]),
        })
        .optional(),
      type: z.object({ eq: newsFeedItemTypeSchema }).optional(),
      workerId: z
        .object({
          eq: z.string(),
        })
        .optional(),
    })
    .optional(),
  include: z.string().optional(),
  page: z
    .object({
      cursor: z.string().optional(),
      size: z.number().optional(),
    })
    .optional(),
});
export type NewsFeedItemsParams = z.infer<typeof newsFeedItemsParamsSchema>;

export const newsFeedItemsLinksSchema = z
  .object({
    nextCursor: z.string().optional(),
    next: z.string().optional(),
    prevCursor: z.string().optional(),
    prev: z.string().optional(),
  })
  .optional();
export type NewsFeedItemsLinks = z.infer<typeof newsFeedItemsLinksSchema>;

export const newsFeedItemsResponseSchema = z.object({
  data: z.array(newsFeedItemSchema),
  included: newsFeedItemIncludedSchema,
  links: newsFeedItemsLinksSchema,
});
export type NewsFeedItemsResponse = z.infer<typeof newsFeedItemsResponseSchema>;

export function useGetNewsFeedItems(
  params: NewsFeedItemsParams = {},
  options: UseInfiniteGetQueryOptions<NewsFeedItemsResponse, AxiosError, NewsFeedItemsResponse> = {}
) {
  const isNewsFeedEnabled = useIsNewsFeedEnabled();

  return useInfiniteGetQuery<
    NewsFeedItemsParams,
    NewsFeedItemsResponse,
    NewsFeedItemsResponse,
    PaginationPageParameters
  >({
    url: NEWS_FEED_URL,
    queryParams: params,
    requestSchema: newsFeedItemsParamsSchema,
    responseSchema: newsFeedItemsResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_NEWS_FEED_ITEMS_FAILURE,
    },
    nextPageContext: getPaginationNextPageContext,
    getNextPageParam: getPaginationNextPageParameters,
    ...options,
    enabled: isNewsFeedEnabled && (options.enabled ?? true),
  });
}
